import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import StripeOnboardingConfirmDialog from '@/components/StripeOnboardingConfirmDialog';
import {
  Wallet,
  CheckCircle,
  Clock,
  ExternalLink,
  Loader2,
  AlertCircle,
  PoundSterling,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DriverPayoutSetup = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [showOnboardConfirm, setShowOnboardConfirm] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Get Connect status
      const statusRes = await axios.get(`${API}/drivers/stripe-connect/status`, { headers });
      setConnectStatus(statusRes.data);

      // Get earnings
      const earningsRes = await axios.get(`${API}/drivers/earnings`, { headers });
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error('Error loading payout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const currentUrl = window.location.origin;
      
      const response = await axios.post(`${API}/drivers/stripe-connect/onboard`, {
        return_url: `${currentUrl}/profile?stripe_return=true`,
        refresh_url: `${currentUrl}/profile?stripe_refresh=true`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.already_onboarded) {
        toast.success('Your payout account is already set up!');
        setShowOnboardConfirm(false);
        loadData();
      } else if (response.data.onboarding_url) {
        // Redirect to Stripe
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start onboarding';
      toast.error(message);
    } finally {
      setOnboardingLoading(false);
    }
  };

  const openDashboard = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/drivers/stripe-connect/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, '_blank');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to open dashboard';
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const isSetupComplete = connectStatus?.status === 'complete';

  return (
    <div className="space-y-4">
      {/* Payout Account Status */}
      <Card className="border-border/50 bg-card/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">Driver Payouts</CardTitle>
                <CardDescription>
                  Receive payments directly to your bank account
                </CardDescription>
              </div>
            </div>
            {isSetupComplete ? (
              <Badge className="bg-primary/20 text-primary border-primary/30">
                <CheckCircle className="w-3 h-3 mr-1" />
                Active
              </Badge>
            ) : (
              <Badge variant="outline" className="border-primary/50 text-primary">
                <Clock className="w-3 h-3 mr-1" />
                Setup Required
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isSetupComplete ? (
            <>
              <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground mb-1">
                      Set up your payout account
                    </p>
                    <p className="text-muted-foreground">
                      Connect your bank account to receive payments from riders. 
                      You'll receive contributions 1 hour after each trip is completed.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => setShowOnboardConfirm(true)}
                disabled={onboardingLoading}
                className="w-full rounded-full"
                data-testid="setup-payout-btn"
              >
                {onboardingLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Wallet className="w-4 h-4 mr-2" />
                )}
                {connectStatus?.status === 'pending' ? 'Continue Setup' : 'Set Up Payouts'}
              </Button>
              <StripeOnboardingConfirmDialog
                open={showOnboardConfirm}
                onOpenChange={setShowOnboardConfirm}
                onConfirm={startOnboarding}
                loading={onboardingLoading}
                audience="driver"
              />
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-xs text-muted-foreground mb-1">Bank Connected</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="font-medium text-primary">Verified</span>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-xs text-muted-foreground mb-1">Payout Timing</p>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="font-medium">1 hr after trip</span>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={openDashboard}
                className="w-full rounded-full"
                data-testid="stripe-dashboard-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Stripe Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Earnings Summary */}
      {earnings && (
        <Card className="border-border/50 bg-card/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg">Your Contributions</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold text-foreground">{earnings.total_earned}</p>
                <p className="text-xs text-muted-foreground">Total Received</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-secondary/10">
                <p className="text-2xl font-bold text-secondary">{earnings.pending_payout}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-primary/10">
                <p className="text-2xl font-bold text-primary">{earnings.completed_payout}</p>
                <p className="text-xs text-muted-foreground">Paid Out</p>
              </div>
            </div>
            {earnings.transaction_count > 0 && (
              <p className="text-xs text-muted-foreground text-center mt-4">
                From {earnings.transaction_count} completed trip{earnings.transaction_count !== 1 ? 's' : ''}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DriverPayoutSetup;
